import type { CommandItem } from 'cmdk-engine'
import { isValidElement } from 'react'
import { Navigate } from 'router-v6'
import type { IRoute, SidebarGroups } from 'v2/types'

const formatGroupLabel = (group: SidebarGroups): string =>
  group
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const joinPath = (parent: string, segment: string): string => {
  if (segment.startsWith('http')) return segment
  const full = segment.startsWith('/') ? segment : `${parent}/${segment}`
  return full.replace(/\/+/g, '/')
}

// Search synonyms that don't appear in a route's own path segments — e.g. Agents
// is Plivo's rebrand of PHLO/flow, so users searching either old term should
// still land on it. Keyed by the command's resolved (joined) path.
const EXTRA_KEYWORDS: Record<string, string[]> = {
  '/agents': ['flow', 'phlo'],
}

// Destinations kept out of the palette entirely. Deprecated (SER-5940): Secret
// Variables is registered on two paths (/agent/secrets + /secrets) and Team
// setup's legacy tab is superseded. Audio Streaming stays a real, directly
// navigable page (and sidebar entry) but should not surface as a searchable
// command. None of these should appear as a command.
const EXCLUDED_COMMAND_PATHS = new Set([
  '/agent/secrets',
  '/secrets',
  '/team-setup/teams',
  '/audio-streaming',
])

// Old paths that now just forward to a real destination (SER-5859 moved several
// pages under Organization settings) render `<Navigate to="..." replace />` as
// their component. They still carry a title/icon for routing purposes, so
// without this check they'd show up as a second, dead-end command right next
// to the real destination (e.g. "Team Setup" the redirect + "Team setup" the
// actual tab). Comparing element `type` (not the path string) keeps this
// robust to `to=` targets changing.
const isRedirectRoute = (route: IRoute): boolean =>
  'component' in route && isValidElement(route.component) && route.component.type === Navigate

// RBAC/visibility gates — a route filtered out for one of these reasons hides its entire
// subtree (see `walk` below), unlike the purely structural skip reasons in `isSkippable`
// (missing title, dynamic segments, redirects), whose children are still real, independently
// reachable pages.
const isAccessFiltered = (route: IRoute): boolean => {
  if (route.notAllowed) return true
  if (route.hasAccess === false) return true
  if ('sidebarOnly' in route && route.sidebarOnly) return true
  // Plan-locked (Enterprise-gated) destinations. The sidebar renders these ghosted with a
  // lock and swaps navigation for the upgrade flow, but the palette has no lock affordance
  // — it deliberately renders icon-less, uniform text rows — and `select()` navigates for
  // any href-bearing item. Left in, searching "Verify" would land the user on a page that
  // immediately bounces or shows an upgrade card, reading as a bug rather than a paywall.
  // So treat the lock like RBAC and withhold the command; the sidebar still carries the
  // upsell. Subtree too: a locked parent's children are equally unreachable.
  if (route.locked) return true
  return false
}

// Routes that should never become a command at all — as opposed to `hidden` routes
// (settings leaves etc.), which ARE emitted but flagged so the palette can keep them
// out of the idle browse list while still matching search. See `walk` below.
const isSkippable = (route: IRoute, fullPath: string): boolean => {
  if (isAccessFiltered(route)) return true
  if (!route.title || !route.path) return true
  if (fullPath.startsWith('http')) return true
  if (fullPath.includes('/:')) return true
  if (isRedirectRoute(route)) return true
  if (EXCLUDED_COMMAND_PATHS.has(fullPath)) return true
  // A route only becomes a real <Route> if it has a component (see flattenRoutes);
  // component-less parent/section routes (Billing, Platform, ...) 404 on direct nav,
  // so they must never surface as commands. Their real children still emit.
  if (!('component' in route) || !route.component) return true
  return false
}

const walk = (
  routes: IRoute[],
  parentPath: string,
  parentGroup: SidebarGroups | undefined,
  out: CommandItem[],
): void => {
  for (const route of routes) {
    // A parent hidden by RBAC (notAllowed/hasAccess) or sidebarOnly must hide its whole
    // subtree — otherwise a restricted section (e.g. Billing) can still leak its children
    // into the palette individually, even though each child's own gate may differ.
    if (isAccessFiltered(route)) continue

    const fullPath = route.path ? joinPath(parentPath, route.path) : parentPath
    const group = route.sidebar?.group ?? parentGroup

    if (route.path && !isSkippable(route, fullPath)) {
      out.push({
        id: fullPath,
        label: route.title === 'Applications' ? 'Apps' : route.title,
        href: fullPath,
        icon: route.icon,
        group: group ? formatGroupLabel(group) : 'Pages',
        keywords: [...fullPath.split('/').filter(Boolean), ...(EXTRA_KEYWORDS[fullPath] ?? [])],
        // Hidden routes (e.g. /profile/2fa, /billing/invoice-and-receipt/invoices) stay
        // searchable but are excluded from the idle browse list — they're still real,
        // reachable pages, just not ones we want cluttering the unfiltered command list.
        hidden: 'hidden' in route ? Boolean(route.hidden) : false,
      })
    }

    if ('children' in route && route.children?.length) {
      walk(route.children, fullPath, group, out)
    }
  }
}

// Which of two same-label commands to keep: prefer a visible entry over a
// hidden one, then an icon-bearing one, then the shorter path (usually the
// canonical top-level route).
const isPreferredCommand = (candidate: CommandItem, current: CommandItem): boolean => {
  if (Boolean(candidate.hidden) !== Boolean(current.hidden)) return !candidate.hidden
  if (Boolean(candidate.icon) !== Boolean(current.icon)) return Boolean(candidate.icon)
  return candidate.id.length < current.id.length
}

export const routesToCommands = (routes: IRoute[]): CommandItem[] => {
  const out: CommandItem[] = []
  walk(routes, '', undefined, out)

  // Dedupe by resolved path so the same destination can never appear twice,
  // keeping the preferred entry (visible > hidden, then icon-bearing) — same
  // rule the label dedupe below uses, so ordering can't discard a better one.
  const byId = new Map<string, CommandItem>()
  for (const command of out) {
    const existing = byId.get(command.id)
    if (!existing || isPreferredCommand(command, existing)) {
      byId.set(command.id, command)
    }
  }

  // Then collapse duplicate labels WITHIN THE SAME GROUP to a single command
  // (SER-5940) — the same page can be emitted from more than one route/path
  // (e.g. Knowledge Bases list + /new). Keying by label alone was too broad: it
  // dropped genuinely different pages that merely share a title but live in
  // different sidebar sections — e.g. the "Conversations" inbox (Views) vs the
  // "Conversations" workload-access settings page. The group disambiguates them,
  // so both stay reachable while true same-section dupes still merge.
  const byLabelGroup = new Map<string, CommandItem>()
  for (const command of byId.values()) {
    const key = `${command.label}\u0000${command.group}`
    const existing = byLabelGroup.get(key)
    if (!existing || isPreferredCommand(command, existing)) {
      byLabelGroup.set(key, command)
    }
  }
  return Array.from(byLabelGroup.values())
}
